import { Grid, makeStyles, InputField, InputLabel, RadioButtons } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import {
  KeypadInputField,
  OverridableInputField,
  OverridableTextArea,
  FormSection,
  useClientOptions
} from '@paintscout/ui-business'
import type { EditorRef } from '@paintscout/ui-business'
import type { QuoteItemSection, UpdateableArea } from '@paintscout/util/builder'
import { getObjectLabels, calculateDimensions } from '@paintscout/util/builder'
import set from 'lodash/set'
import React, { useState } from 'react'
import classNames from 'classnames'
import { useQuote } from '../../../../context'

export interface AreaDetailsFormProps {
  area: UpdateableArea
  section: QuoteItemSection
  short?: boolean
  hideLabel?: boolean
  onChange?: (event: any, area: UpdateableArea) => void
  descriptionRef?: EditorRef
}
const useStyles = makeStyles<Theme, { collapseOpen: boolean }>((theme) => ({
  root: {
    minHeight: 350,
    [theme.breakpoints.down('sm')]: {
      minHeight: 0
    }
  },
  areaTypeSectionLabel: {
    paddingBottom: theme.spacing(4)
  },
  areaNameSectionTitle: {
    paddingBottom: theme.spacing(2)
  },
  calculatedAreaSection: {
    marginTop: theme.typography.pxToRem(5),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    padding: '10px',
    borderRadius: theme.borderRadius.md
  },
  calculatedAreaSectionInner: {
    padding: '10px'
  },
  descriptionSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(3)
  },
  labelInput: {
    '& input': {
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  radioButton: {
    height: '22px'
  },
  dimensionInput: {},
  radioButtons: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%'
    }
  },
  radioLabel: {
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0
    }
  },
  radioItemLabel: {
    marginBottom: 0
  },
  radioGroup: {
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginTop: 0
    }
  },
  htmlEditor: {
    width: 555,
    boxSizing: 'content-box'
  }
}))

export default function AreaDetailsForm(props: AreaDetailsFormProps) {
  const { short, hideLabel, descriptionRef, section } = props

  const { options } = useClientOptions()

  const { substrate, unit } = getObjectLabels({ options })
  const [area, setArea] = useState(props.area)
  const { isEditable } = useQuote()
  const disabled = !isEditable && ['bid', 'additional', 'added-options'].includes(section)
  const classes = useStyles({ collapseOpen: true })

  return (
    <div className={classes.root}>
      <FormSection hideDivider classes={{ root: classes.areaTypeSectionLabel }}>
        <Grid container alignItems="center" justifyContent="flex-start" spacing={2} className={classes.toggles}>
          <Grid item xs={12}>
            <RadioButtons
              classes={{
                root: classes.radioButtons,
                label: classes.radioLabel,
                radioLabel: classes.radioItemLabel,
                radioGroup: classes.radioGroup
              }}
              label={'Area Type'}
              onChange={handleAreaTypeChange}
              disabled={disabled}
              options={[
                { name: 'room', label: 'Room (4 Walls)' },
                { name: 'surface', label: 'Surface (Single-Wall)' }
              ]}
              value={area.areaType}
            />
          </Grid>
        </Grid>
      </FormSection>
      {(!short || !hideLabel) && (
        <FormSection hideDivider classes={{ root: classes.areaNameSectionTitle }}>
          <Grid container spacing={2} alignItems="flex-end" justifyContent="center">
            <Grid item xs={12} md={6}>
              <InputField
                label={'Area Name'}
                onChange={handleChange}
                name={'label'}
                type={'text'}
                disabled={disabled}
                value={area.label}
                fullWidth={true}
                autoFocus={short}
                classes={{ root: classes.labelInput }}
              />
            </Grid>

            {area.areaType === 'room' && (
              <Grid item xs={12} md={2}>
                <KeypadInputField
                  format={'dimension'}
                  classes={{ root: classNames(classes.dimensionInput, 'tour-area-details-width-field') }}
                  label={`Width (${unit.abbreviation.basic})`}
                  autoSelect={true}
                  name={'dimensions.width'}
                  value={area.dimensions.width}
                  onChange={handleChange}
                  fullWidth={true}
                  disabled={disabled}
                />
              </Grid>
            )}

            <Grid item xs={12} md={area.areaType === 'room' ? 2 : 3}>
              <KeypadInputField
                format={'dimension'}
                classes={{ root: classes.dimensionInput }}
                label={`Length (${unit.abbreviation.basic})`}
                autoSelect={true}
                disabled={disabled}
                name={'dimensions.length'}
                value={area.dimensions.length}
                onChange={handleChange}
                fullWidth={true}
              />
            </Grid>
            <Grid item xs={12} md={area.areaType === 'room' ? 2 : 3}>
              <KeypadInputField
                format={'dimension'}
                classes={{ root: classes.dimensionInput }}
                label={`${area.areaType === 'room' ? 'Height' : 'Height/width'} (${unit.abbreviation.basic})`}
                autoSelect={true}
                flipX={true}
                disabled={disabled}
                name={'dimensions.height'}
                value={area.dimensions.height}
                onChange={handleChange}
                fullWidth={true}
              />
            </Grid>
          </Grid>
        </FormSection>
      )}
      <div className={'showDimensions'}>
        <FormSection hideDivider>
          <InputLabel disabled={disabled}>Calculated From Measurements</InputLabel>
          <Grid container md={area.areaType === 'surface' ? 6 : 12} classes={{ root: classes.calculatedAreaSection }}>
            <Grid container item spacing={2} classes={{ root: classes.calculatedAreaSectionInner }}>
              <Grid item xs={6} md={area.areaType === 'surface' ? 6 : 3}>
                <OverridableInputField
                  format={'dimension'}
                  label={`Linear (${unit.abbreviation.basic})`}
                  name={'lnft'}
                  disabled={disabled}
                  value={area.dimensions.lnft}
                  resetBlank={true}
                  onChange={handleOverrideDimension('lnft')}
                  fullWidth={true}
                  autoSelect={true}
                />
              </Grid>
              {area.areaType === 'surface' && (
                <Grid item xs={6} md={area.areaType === 'surface' ? 6 : 3}>
                  <OverridableInputField
                    format={'dimension'}
                    label={unit.abbreviation.square.toUpperCase()}
                    name={'sqft'}
                    disabled={disabled}
                    value={area.dimensions.sqft}
                    resetBlank={true}
                    onChange={handleOverrideDimension('sqft')}
                    fullWidth={true}
                    autoSelect={true}
                  />
                </Grid>
              )}
              {area.areaType === 'room' && (
                <Grid item xs={6} md={3}>
                  <OverridableInputField
                    format={'dimension'}
                    label={`Walls (${unit.abbreviation.square.toUpperCase()})`}
                    name={'sqftWalls'}
                    value={area.dimensions.sqftWalls}
                    resetBlank={true}
                    disabled={disabled}
                    onChange={handleOverrideDimension('sqftWalls')}
                    fullWidth={true}
                    autoSelect={true}
                  />
                </Grid>
              )}
              {area.areaType === 'room' && (
                <Grid item xs={6} md={3}>
                  <OverridableInputField
                    format={'dimension'}
                    label={`Ceiling (${unit.abbreviation.square.toUpperCase()})`}
                    name={'sqftCeiling'}
                    disabled={disabled}
                    value={area.dimensions.sqftCeiling}
                    resetBlank={true}
                    onChange={handleOverrideDimension('sqftCeiling')}
                    fullWidth={true}
                    autoSelect={true}
                  />
                </Grid>
              )}
              {area.areaType === 'room' && (
                <Grid item xs={6} md={3}>
                  <OverridableInputField
                    format={'dimension'}
                    label={`Floor (${unit.abbreviation.square.toUpperCase()})`}
                    name={'sqftFloor'}
                    value={area.dimensions.sqftFloor}
                    resetBlank={true}
                    disabled={disabled}
                    onChange={handleOverrideDimension('sqftFloor')}
                    fullWidth={true}
                    flipX={true}
                    autoSelect={true}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </FormSection>
      </div>
      <FormSection
        classes={{
          root: classNames(classes.descriptionSection, 'showDescription')
        }}
        style={{ paddingTop: 0 }}
        hideDivider={true}
      >
        <OverridableTextArea
          label={'Description'}
          allowFormatting={true}
          sublabel={`Unless specified, the area description will be generated based on ${substrate.plural.toLowerCase()} added.`}
          disabled={disabled}
          value={area.description}
          classes={{ htmlEditor: classes.htmlEditor }}
          ref={descriptionRef}
        />
      </FormSection>
    </div>
  )

  function handleOverrideDimension(name: string) {
    return function (value: { useCustom: boolean; custom: number | string; default: number | string }) {
      const newArea = {
        ...area,
        dimensions: {
          ...area.dimensions,
          [name]: value
        }
      }

      if (props.onChange) {
        props.onChange(event, newArea)
      }
      setArea(newArea)
    }
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value, type } = event.target

    const newArea = {
      ...area
    }

    if (
      (name === 'dimensions.length' || name === 'dimensions.width' || name === 'dimensions.height') &&
      !isNaN(parseInt(value, 20))
    ) {
      set(newArea, name, Number(value))
    } else if (type === 'checkbox') {
      const { checked } = event.target
      set(newArea, name, !checked)
    } else {
      set(newArea, name, value)
    }

    if (type !== 'number' || !isNaN(parseInt(value, 10))) {
      newArea.dimensions = calculateDimensions({ dimensions: newArea.dimensions, areaType: newArea.areaType })

      if (props.onChange) {
        props.onChange(event, newArea)
      }
    }

    setArea(newArea)
  }

  function handleAreaTypeChange(areaType: 'room' | 'surface') {
    const newArea = {
      ...area,
      areaType
    }

    newArea.dimensions = calculateDimensions({ dimensions: newArea.dimensions, areaType })

    setArea(newArea)

    if (props.onChange) {
      props.onChange(null, newArea)
    }
  }
}
